// SSE 이벤트→캐시 무효화 "결정 로직"의 순수 추출본. 어떤 쿼리 키를 무효화할지를
// payload만 보고 plain 데이터로 결정한다. coalescing 타이머·queryClient 부수효과는
// RealtimeEventsProvider가 그대로 보유하고, 이 모듈은 결정만 담당한다.
// 회귀가 가장 잦았던 구역(plan.updated 좁은 스코프, daemonTrigger terminal→past)을
// 격리 단위 테스트로 고정하기 위해 RealtimeEventsProvider에서 분리했다.

import { TERMINAL_DAEMON_TRIGGER_STATUSES, type DaemonTriggerTerminalStatus } from '@agentteams/realtime-contract';
import { queryKeys } from '../hooks/queries/queryKeys';
import { getStringField } from './realtimePayload';

// 종료(terminal) 상태일 때만 "이전 러너"(past) 목록을 갱신한다. 진행/대기 이벤트는
// active 목록만 친다 → 종료된 러너 목록의 불필요한 재조회/재정렬(클릭 삼킴) 방지.
// 종료 상태 집합은 api `DaemonTriggerStatus`의 종료 상태와 공유 계약
// (@agentteams/realtime-contract)에서 단일 출처로 관리한다 — 재export해 기존 import 경로를 유지한다.
export { TERMINAL_DAEMON_TRIGGER_STATUSES };

export type PlanInvalidation = {
  projectId: string | null;
  keys: readonly (readonly unknown[])[];
};

// LNB 배지 집계에 영향을 주는 필드의 엔티티별 마지막 관측값(키: `plan:<id>`, `trigger:<id>`).
// 러너 로그 append처럼 집계와 무관한 같은 상태 이벤트가 배지 API를 3초마다 재조회시키지 않도록,
// 관측값이 바뀐 이벤트에만 배지 키를 붙인다. 호출 측(RealtimeEventsProvider)이 연결 수명 동안 보유한다.
export type BadgeFieldSnapshots = Map<string, Record<string, string | null>>;

// 처음 보는 엔티티, 또는 payload에 실린 필드 중 하나라도 직전 관측값과 다르면 true. 실리지 않은
// 필드는 판정에서 빠진다(이벤트마다 싣는 필드가 다르다). 관측값은 이 호출에서 갱신한다.
export const hasBadgeFieldChange = (
  snapshots: BadgeFieldSnapshots,
  entityKey: string,
  payload: unknown,
  fields: readonly string[],
): boolean => {
  const previous = snapshots.get(entityKey);
  const next: Record<string, string | null> = { ...previous };
  let changed = previous === undefined;
  const record = payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : {};
  for (const field of fields) {
    if (!(field in record)) continue;
    const value = getStringField(payload, field);
    if (!previous || !(field in previous) || previous[field] !== value) changed = true;
    next[field] = value;
  }
  snapshots.set(entityKey, next);
  return changed;
};

// 플랜 집계(IN_PROGRESS·가시성)와 연결 산출물의 상속 가시성을 바꿀 수 있는 필드.
const PLAN_BADGE_FIELDS = ['status', 'visibility', 'assigneeMemberId'] as const;
// 러너 집계는 활성 상태 여부만 본다.
const DAEMON_TRIGGER_BADGE_FIELDS = ['status'] as const;

export type DaemonTriggerInvalidation = {
  triggerId: string | null;
  includeTerminal: boolean;
  keys: readonly (readonly unknown[])[];
};

// plan.updated는 다른 도메인과 달리 plans.all 전체를 무효화하지 않는다. plans.all은
// 열린 상세 패널의 fullDetail까지 매 이벤트마다 refetch시켜
// 멤버 rate-limit(분당 100)을 소진시키는 폭주원이었다. 리스트/무한스크롤 키만 갱신하고,
// 상세는 실제 업데이트된 planId만 정밀 무효화한다.
// 배지 키는 snapshots가 주어지면 집계가 바뀔 수 있는 이벤트(삭제, 상태·가시성·담당자 변화)에만 붙인다.
// 태스크 상태 갱신·정책 변경처럼 플랜 상태가 그대로인 이벤트는 제외한다.
export const resolvePlanInvalidation = (payload: unknown, snapshots?: BadgeFieldSnapshots): PlanInvalidation => {
  const projectId = getStringField(payload, 'projectId');
  if (!projectId) return { projectId: null, keys: [] };

  const keys: (readonly unknown[])[] = [queryKeys.plans.lists(projectId), ['plans', projectId, 'infinite']];

  const planId = getStringField(payload, 'planId');
  if (planId) {
    keys.push(queryKeys.plans.fullDetail(projectId, planId));
  }

  const affectsBadges =
    !snapshots ||
    !planId ||
    getStringField(payload, 'deletedAt') !== null ||
    hasBadgeFieldChange(snapshots, `plan:${planId}`, payload, PLAN_BADGE_FIELDS);
  if (affectsBadges) {
    keys.push(queryKeys.navigationBadges.project(projectId));
  }

  return { projectId, keys };
};

// 일반 프로젝트 도메인(completionReport/postMortem/coAction/codeReview/document/convention).
// projectId가 없으면 도메인 갱신은 skip한다(홈 피드 갱신은 호출 측에서 유지).
export const resolveProjectDomainInvalidation = (
  payload: unknown,
  queryKeyForProject: (projectId: string) => readonly unknown[],
): readonly unknown[] | null => {
  const projectId = getStringField(payload, 'projectId');
  if (!projectId) return null;
  return queryKeyForProject(projectId);
};

// daemonTrigger payload의 status가 terminal이면 past 목록까지 갱신해야 한다.
export const isTerminalDaemonStatus = (payload: unknown): boolean => {
  const status = getStringField(payload, 'status');
  // status는 임의 문자열이므로 종료 상태 union으로 좁혀 멤버십을 검사한다(미일치 시 false).
  return status !== null && TERMINAL_DAEMON_TRIGGER_STATUSES.has(status as DaemonTriggerTerminalStatus);
};

// 배지 키는 snapshots가 주어지면 상태가 바뀐 이벤트에만 붙인다 — 로그 append는 같은 상태로 발행된다.
export const resolveDaemonTriggerInvalidation = (
  payload: unknown,
  snapshots?: BadgeFieldSnapshots,
): DaemonTriggerInvalidation => {
  const triggerId = getStringField(payload, 'triggerId');
  // 워크트리는 트리거 생명주기(시작 시 생성, 종료 시 제거 대상)에 따라 상태가 바뀌므로,
  // 다른 세션이 트리거를 변경하면 SSE로도 워크트리 뷰(목록/활성)를 갱신해야 한다. payload에
  // projectId/agentConfigId가 없어 정밀 키를 못 만드는 경우가 있어 prefix로 일괄 무효화한다.
  // logs(자체 스트리밍)와 injectedConventions(트리거 생성 시 고정, 이후 불변)는 외부 트리거
  // 상태 변화와 무관하므로 의도적으로 제외한다 — 과거 daemonTriggers.all 무효화가 이 둘까지
  // 매 이벤트 재조회시켜 read starvation을 유발했던 부분이다.
  const keys: (readonly unknown[])[] = [
    queryKeys.daemonTriggers.lists,
    queryKeys.daemonTriggers.infinites,
    queryKeys.daemonTriggers.worktreeLists,
    queryKeys.daemonTriggers.activeWorktreesAll,
  ];

  if (triggerId) {
    keys.push(queryKeys.daemonTriggers.detail(triggerId));
  }

  // 플랜 상세의 실패 정지 안내는 최신 런의 status(BLOCKED)만 보고 갈린다. 그 값을 담은
  // `plans.workflowRuns`는 어떤 실시간 이벤트로도 무효화되지 않아, 화면을 열어 둔 채 마지막 러너
  // 요청이 실패하면 트리거 목록만 SSE로 비고 런 캐시는 RUNNING에 머물러 아무 안내도 뜨지 않았다.
  // 접두사로 넣어 같은 런의 그래프 캐시(원인 태스크 제목)까지 함께 푼다. 두 쿼리 모두 진행 중인
  // 플랜 상세에서만 켜지므로 폭이 넓어도 재조회 대상은 열린 화면 하나뿐이다.
  const projectId = getStringField(payload, 'projectId');
  if (projectId) {
    keys.push(['plans', projectId, 'workflowRuns']);
  }

  const affectsBadges =
    !snapshots ||
    !triggerId ||
    hasBadgeFieldChange(snapshots, `trigger:${triggerId}`, payload, DAEMON_TRIGGER_BADGE_FIELDS);
  if (affectsBadges) {
    // 과거 이벤트에 프로젝트 귀속이 없어도 활성 메뉴 집계는 다음 재연결까지 정체하지 않게 한다.
    keys.push(projectId ? queryKeys.navigationBadges.project(projectId) : queryKeys.navigationBadges.all);
  }

  return {
    triggerId,
    includeTerminal: isTerminalDaemonStatus(payload),
    keys,
  };
};
